import type { Dispatch, ReactNode, SetStateAction } from 'react'
import {
  useState,
  useEffect,
  createContext,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { useAccountContext } from 'src/components/account/context'
import { useToastContext } from 'src/components/ui/ToastMessage/ToastContext'
import { useMobile } from 'src/hooks/useMobile'
import type { DeliveryResponse } from 'src/components/checkout/steps/shipping/types'
import { Statsig } from 'src/sdk/statsig'
import {
  SHARED_CART_EXPERIMENT_ID,
  SIMPLIFIED_SHIPPING_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'

import { axiosClient } from '../../../services/axiosInstance'
import { useCartItems } from '../hooks/useCartItems'
import { useShippingData } from '../hooks/useShippingData'
import { useClientProfile } from '../hooks/useGuestProfile'
import type {
  OrderItemOnAdd,
  OrderItemOnUpdate,
  ClientProfileBody,
  MarketingDataBody,
} from '../data/types'
import type { Item } from '../entity/orderForm'
import type { CustomDataBody } from '../data/types/customData'
import { OrderForm } from '../entity/orderForm'
import { getOrderForm } from '../data/checkout'
import { addCartCoupon } from '../data/checkout/coupon'
import { clearMessages } from '../data/checkout/messages'
import { addCartCustomData } from '../data/checkout/customData'
import { addCartMarketingData } from '../data/checkout/marketingData'
import { storage } from '../data/utils/storage'

interface CheckoutProviderProps {
  children: ReactNode
  paramOrderFormId?: string | null
}

interface ContextValue {
  orderForm: OrderForm
  isEmpty: boolean
  hasBeenInitialized: boolean
  isLoading: boolean
  itemsTotal: number
  addItems: (items: OrderItemOnAdd[], orderFormId?: string) => Promise<void>
  addCoupon: (coupon: string) => Promise<OrderForm>
  addShippingData: (cep: string) => Promise<OrderForm>
  getItems: () => Item[]
  updateItems: (items: OrderItemOnUpdate[]) => Promise<void>
  updateItem: (items: OrderItemOnUpdate) => Promise<OrderForm>
  addCustomData: (body: CustomDataBody, useServer: boolean) => Promise<void>
  clearCart: () => Promise<void>
  addMarketingData: (body: MarketingDataBody) => Promise<void>
  setOrderForm: (orderForm: OrderForm) => void
  checkGuestCheckout: ({
    email,
  }: {
    email: string
  }) => Promise<OrderForm | null>
  addClientProfile: (params: {
    orderFormId: string
    clientProfile: ClientProfileBody
    setOrderForm: Dispatch<SetStateAction<OrderForm>>
  }) => Promise<void>
  updateClientPreferenceData: (optinNewsletter: boolean) => Promise<void>
  getSimulation: () => Promise<DeliveryResponse>
  isCouponOpen: boolean
  setIsCouponOpen: Dispatch<SetStateAction<boolean>>
}

const FILTERED_MESSAGE_CODES = ['changedFreightPrice', 'changedPriceValue']

export const CheckoutContext = createContext<ContextValue>({} as ContextValue)

export const addClientProfile = async ({
  orderFormId,
  clientProfile,
  setOrderForm,
}: {
  orderFormId: string
  clientProfile: ClientProfileBody
  setOrderForm: (orderForm: OrderForm) => void
}) => {
  try {
    const { data } = await axiosClient.post(
      `/api/checkout/orderForm/${orderFormId}/attachments/clientProfileData`,
      {
        ...clientProfile,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    setOrderForm(new OrderForm(data))
  } catch (error) {
    console.error('Erro ao adicionar o perfil do cliente:', error)
  }
}

export const CheckoutProvider = ({
  children,
  paramOrderFormId,
}: CheckoutProviderProps) => {
  const { userDecathlon, isLoggedIn } = useAccountContext()
  const { sendToast } = useToastContext()
  const { isMobile } = useMobile()

  const [orderForm, setOrderForm] = useState<OrderForm>({
    orderFormId: '',
    items: [],
    messages: [],
    totalizers: {},
    clientProfileData: null,
    shippingData: {},
    loggedIn: false,
    clientPreferencesData: {},
    paymentData: {},
    marketingData: {},
    customData: null,
    sellers: [],
  })

  const [hasBeenInitialized, setHasBeenInitialized] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { addItems, updateItem, updateItems } = useCartItems(
    orderForm,
    setOrderForm,
    setIsLoading
  )

  const [isCouponOpen, setIsCouponOpen] = useState(false)

  const { addShippingData } = useShippingData(orderForm, setOrderForm)
  const { checkGuestCheckout } = useClientProfile(orderForm, setOrderForm)
  const isSharedCartEnabled = Statsig.useStatsigFeatureGateValue(
    SHARED_CART_EXPERIMENT_ID
  )

  const isSimplifiedShippingEnabled = Statsig.useStatsigFeatureGateValue(
    SIMPLIFIED_SHIPPING_EXPERIMENT_ID
  )

  const hasSyncedOrderForm = useRef(false)

  const itemsTotal = orderForm?.items?.length ?? 0

  useEffect(() => {
    if (
      !isSharedCartEnabled ||
      !isLoggedIn ||
      !orderForm?.orderFormId ||
      hasSyncedOrderForm.current
    ) {
      return
    }

    hasSyncedOrderForm.current = true

    const syncWithRetry = async (
      retries = 3,
      delayMs = 1000
    ): Promise<void> => {
      try {
        const { data } = await axiosClient.post(
          `/api/checkout/orderForm/${orderForm.orderFormId}/merge/cart`
        )

        const { orderFormUpdateRequired, orderForm: updatedOrderForm } =
          data ?? {}

        if (orderFormUpdateRequired && updatedOrderForm) {
          setOrderForm(new OrderForm(updatedOrderForm))
          storage().setItem('orderFormId', updatedOrderForm.orderFormId)
        }
      } catch (error) {
        if (retries > 0) {
          console.warn(
            `Falha na sincronização. Tentando novamente... (${retries} restantes)`
          )

          await new Promise((res) => setTimeout(res, delayMs))

          return syncWithRetry(retries - 1, delayMs)
        }

        console.error('Limite de tentativas de sincronização atingido:', error)
      }
    }

    syncWithRetry()
  }, [isSharedCartEnabled, orderForm.orderFormId, isLoggedIn])

  // Adds user info
  useEffect(() => {
    if (!orderForm || !orderForm.orderFormId) {
      return
    }

    const validateCheckoutOwner = () => {
      const emailInCart = orderForm.clientProfileData?.email

      if (emailInCart === undefined && emailInCart === null) {
        return
      }

      const emailLoggedIn = userDecathlon?.getUserProfile?.claims?.email

      const cartWithProfileAndLoggedInUserAreNotTheSame =
        emailLoggedIn !== emailInCart

      if (
        cartWithProfileAndLoggedInUserAreNotTheSame &&
        userDecathlon &&
        userDecathlon?.getUserProfile
      ) {
        const cpfValue =
          userDecathlon?.getUserProfile?.additional_information?.find(
            (item) => item?.id === 'cpf'
          )?.value as string

        addClientProfile({
          orderFormId: orderForm.orderFormId,
          clientProfile: {
            document: cpfValue,
            documentType: 'cpf',
            email: userDecathlon?.getUserProfile?.claims?.email,
            firstName: userDecathlon?.getUserProfile?.claims?.given_name,
            lastName: userDecathlon?.getUserProfile?.claims?.middle_name,
            phone: userDecathlon?.getUserProfile?.claims?.phone_number,
          },
          setOrderForm,
        })
      }
    }

    validateCheckoutOwner()
  }, [orderForm, userDecathlon])

  // Handle messages and convert into toasts
  useEffect(() => {
    const handleMessages = async () => {
      if (!orderForm?.messages || !orderForm?.messages?.length) {
        return
      }

      const newMessage = orderForm.messages[orderForm.messages.length - 1]

      if (FILTERED_MESSAGE_CODES.includes(newMessage.code)) {
        await clearMessages(orderForm.orderFormId)

        return
      }

      sendToast({
        text: newMessage.text,
        variant: newMessage.status,
      })

      await clearMessages(orderForm.orderFormId)
    }

    handleMessages()
  }, [orderForm?.messages, orderForm?.orderFormId, sendToast])

  // Fetch initial orderform
  useEffect(() => {
    if (isMobile === undefined) {
      return
    }

    const fetchData = async () => {
      try {
        const baseOrderForm = await getOrderForm(paramOrderFormId ?? '')

        setOrderForm(baseOrderForm)
        setIsLoading(false)
        setHasBeenInitialized(true)

        if (
          baseOrderForm.orderFormId &&
          (!baseOrderForm.marketingData?.utmMedium ||
            !baseOrderForm.marketingData?.utmSource)
        ) {
          await addCartMarketingData(baseOrderForm.orderFormId, {
            ...baseOrderForm.marketingData,
            utmMedium: 'site',
            utmSource: isMobile ? 'mobile' : 'desktop',
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [isMobile, paramOrderFormId])

  const getItems = useCallback(() => {
    if (!orderForm) {
      return []
    }

    return orderForm.items
  }, [orderForm])

  const addCoupon = useCallback(
    async (coupon: string) => {
      const updatedOrderform = await addCartCoupon(
        orderForm.orderFormId,
        coupon
      )

      setOrderForm(updatedOrderform)

      return updatedOrderform
    },
    [orderForm.orderFormId]
  )

  const addCustomDataHandler = useCallback(
    async (data: CustomDataBody, useServer: boolean) => {
      if (!orderForm.orderFormId) return

      if (!useServer) {
        await addCartCustomData(orderForm.orderFormId, data)

        return
      }

      try {
        const response = await axiosClient.post(
          `/api/checkout/orderForm/${orderForm.orderFormId}/customdata/addCustomdata`,
          data
        )

        if (response.data) {
          setOrderForm(new OrderForm(response.data))
        }
      } catch (error) {
        console.error('Erro na API Route:')
      }
    },
    [orderForm.orderFormId]
  )

  const addMarketingDataHandler = useCallback(
    async (data: MarketingDataBody) => {
      if (!orderForm.orderFormId) return

      const prevMarketingData = orderForm.marketingData
      const updatedMarketingTags = Array.from(
        new Set([
          ...(orderForm?.marketingData?.marketingTags ?? []),
          ...(data.marketingTags ?? []),
        ])
      )

      await addCartMarketingData(orderForm.orderFormId, {
        ...prevMarketingData,
        marketingTags: updatedMarketingTags,
      })
    },
    [orderForm.marketingData, orderForm.orderFormId]
  )

  const getSimulation = useCallback(async () => {
    try {
      const simulationUrl = isSimplifiedShippingEnabled
        ? `/api/checkout/orderForm/${orderForm.orderFormId}/simulationv2`
        : `/api/checkout/orderForm/${orderForm.orderFormId}/simulation`

      const { data } = await axiosClient.get(simulationUrl, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })

      return data
    } catch (error) {
      console.error('Error fetching simulation:', error)

      return null
    }
  }, [isSimplifiedShippingEnabled, orderForm.orderFormId])

  const updateClientPreferenceData = useCallback(
    async (optinNewsletter: boolean) => {
      const { data: updatedOrderform } = await axiosClient.post(
        `/api/checkout/orderForm/${orderForm.orderFormId}/attachments/preferenceData`,
        {
          optinNewsletter,
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      )

      setOrderForm(updatedOrderform)
    },
    [orderForm.orderFormId]
  )

  const clearCart = useCallback(async () => {
    if (!orderForm || !orderForm?.items?.length) {
      return
    }

    setIsLoading(true)
    // Remove todos os itens do carrinho
    await updateItems(
      orderForm.items.map((item) => ({
        index: item.itemIndex,
        quantity: 0,
      }))
    )
    // Atualiza o estado do carrinho
    const updatedOrderform = await getOrderForm(orderForm.orderFormId)

    setOrderForm(updatedOrderform)
    setIsLoading(false)
  }, [orderForm, updateItems])

  const value = useMemo((): ContextValue => {
    const isEmpty = hasBeenInitialized && orderForm?.items?.length === 0

    return {
      hasBeenInitialized,
      orderForm,
      isEmpty,
      isLoading,
      clearCart,
      addItems,
      getItems,
      addCoupon,
      addShippingData,
      updateItem,
      updateItems,
      itemsTotal,
      addCustomData: addCustomDataHandler,
      setOrderForm,
      checkGuestCheckout,
      addClientProfile,
      updateClientPreferenceData,
      getSimulation,
      isCouponOpen,
      setIsCouponOpen,
      addMarketingData: addMarketingDataHandler,
    }
  }, [
    hasBeenInitialized,
    orderForm,
    isLoading,
    clearCart,
    addItems,
    getItems,
    addCoupon,
    addShippingData,
    updateItem,
    updateItems,
    itemsTotal,
    addCustomDataHandler,
    checkGuestCheckout,
    updateClientPreferenceData,
    getSimulation,
    isCouponOpen,
    addMarketingDataHandler,
  ])

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  )
}
